/**
 * Команда портала (ЛЦ-09): владельцы и администраторы портала. Назначает и снимает только владелец портала;
 * последнего владельца снять нельзя. Изменения — в журнале портала и письмом владельцам.
 */
import {
  PLATFORM_ROLE_HINTS,
  PLATFORM_ROLE_TITLES,
  PLATFORM_ROLES,
  type PlatformRole,
  type PlatformTeamMember,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { dateTime, relative } from '../../lib/format';
import { useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Select } from '../../ui/form';
import { Panel } from '../../ui/layout';

const KEY = ['platform', 'team'];

function AddForm() {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<PlatformRole>('admin');
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const add = useMutation({
    mutationFn: () =>
      api<PlatformTeamMember>('/platform/team', { body: { email, role, note: note || null } }),
    onSuccess: (m) => {
      void qc.invalidateQueries({ queryKey: KEY });
      toast(`${m.fullName} — ${PLATFORM_ROLE_TITLES[m.role].toLowerCase()}`);
      setEmail('');
      setNote('');
    },
    onError: (err) => err instanceof ApiError && setErrors(err.fieldErrors()),
  });
  function submit(e: FormEvent) {
    e.preventDefault();
    setErrors({});
    add.mutate();
  }
  return (
    <Panel title="Добавить в команду">
      <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2" noValidate>
        <Field
          label="Почта учётной записи"
          htmlFor="team-email"
          hint="Человек уже должен работать в портале хотя бы в одной компании"
          error={errors.email}
          className="sm:col-span-2"
        >
          <Input
            id="team-email"
            type="email"
            inputMode="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            invalid={!!errors.email}
          />
        </Field>
        <Field label="Роль" htmlFor="team-role" hint={PLATFORM_ROLE_HINTS[role]}>
          <Select id="team-role" value={role} onChange={(e) => setRole(e.target.value as PlatformRole)}>
            {[...PLATFORM_ROLES].reverse().map((r) => (
              <option key={r} value={r}>
                {PLATFORM_ROLE_TITLES[r]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Заметка" htmlFor="team-note" hint="Например: сервер и инфраструктура">
          <Input id="team-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} />
        </Field>
        <div className="sm:col-span-2">
          <Button variant="primary" type="submit" loading={add.isPending} disabled={!email.trim()}>
            Добавить
          </Button>
        </div>
        <ErrorBox className="sm:col-span-2" error={Object.keys(errors).length ? null : add.error} />
      </form>
    </Panel>
  );
}

export function TeamPage() {
  const me = useMe();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: KEY, queryFn: () => api<PlatformTeamMember[]>('/platform/team') });
  const [removing, setRemoving] = useState<PlatformTeamMember | null>(null);
  const change = useMutation({
    mutationFn: (v: { userId: string; role: PlatformRole | null }) =>
      api(`/platform/team/${v.userId}`, { method: 'PATCH', body: { role: v.role } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      void qc.invalidateQueries({ queryKey: ['me'] });
      setRemoving(null);
      toast('Команда портала изменена');
    },
    onError: (err) => toast(err instanceof Error ? err.message : 'Не удалось', 'danger'),
  });
  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
      <Panel title="Команда портала" meta={q.data ? `${q.data.length}` : undefined} bodyClassName="p-0">
        {q.isPending ? (
          <div className="px-5">
            <Spinner />
          </div>
        ) : q.isError ? (
          <div className="p-5">
            <ErrorBox error={q.error} />
          </div>
        ) : (
          <ul className="divide-y divide-line">
            {q.data.map((m) => (
              <li
                key={m.userId}
                className="flex flex-col gap-2 px-5 py-3 sm:flex-row sm:items-center sm:gap-4"
              >
                <div className="min-w-0 flex-1 text-[14px]">
                  <div className="flex flex-wrap items-center gap-2">
                    <Link
                      to="/platform/users/$id"
                      params={{ id: m.userId }}
                      className="inline-flex min-h-11 items-center font-medium text-ink underline-offset-2 hover:underline md:min-h-0"
                    >
                      {m.fullName}
                    </Link>
                    {m.userId === me.user.id ? <Badge>это вы</Badge> : null}
                    {!m.isActive ? <Badge tone="danger">вход заблокирован</Badge> : null}
                  </div>
                  <div className="text-[13px] text-ink-2 [overflow-wrap:anywhere]">
                    {m.email} · последний вход {relative(m.lastLoginAt)}
                    {m.since ? ` · в команде с ${dateTime(m.since)}` : ''}
                    {m.addedByName ? `, добавил(а) ${m.addedByName}` : ''}
                    {m.note ? ` · ${m.note}` : ''}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Select
                    aria-label={`Роль: ${m.fullName}`}
                    value={m.role}
                    className="w-full sm:w-56"
                    disabled={change.isPending}
                    onChange={(e) =>
                      change.mutate({ userId: m.userId, role: e.target.value as PlatformRole })
                    }
                  >
                    {PLATFORM_ROLES.map((r) => (
                      <option key={r} value={r}>
                        {PLATFORM_ROLE_TITLES[r]}
                      </option>
                    ))}
                  </Select>
                  <Button size="sm" variant="ghost" onClick={() => setRemoving(m)}>
                    Убрать
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Panel>
      <div className="flex flex-col gap-5">
        <AddForm />
        <Panel title="Что может каждая роль">
          <dl className="flex flex-col gap-3 text-[14px]">
            {PLATFORM_ROLES.map((r) => (
              <div key={r}>
                <dt className="font-medium text-ink">{PLATFORM_ROLE_TITLES[r]}</dt>
                <dd className="text-[13px] text-ink-2">{PLATFORM_ROLE_HINTS[r]}</dd>
              </div>
            ))}
          </dl>
        </Panel>
      </div>
      <Dialog
        open={Boolean(removing)}
        onOpenChange={(v) => !v && setRemoving(null)}
        width="sm"
        title={`Убрать из команды: ${removing?.fullName ?? ''}?`}
        description="Кабинет портала у человека закроется, входы в чужие компании закончатся. В своих компаниях он продолжит работать."
        footer={
          <>
            <Button variant="ghost" onClick={() => setRemoving(null)}>
              Отмена
            </Button>
            <Button
              variant="danger"
              loading={change.isPending}
              onClick={() => removing && change.mutate({ userId: removing.userId, role: null })}
            >
              Убрать
            </Button>
          </>
        }
      >
        <p className="text-[14px] text-ink-2">Вернуть можно в любой момент — добавлением по почте.</p>
      </Dialog>
    </div>
  );
}
